import {
  ApiBlueprint,
  configApiRef,
  createApiFactory,
  createApiRef,
  createFrontendModule,
  discoveryApiRef,
  oauthRequestApiRef,
} from '@backstage/frontend-plugin-api';
import { OAuth2 } from '@backstage/core-app-api';
import { SignInPageBlueprint } from '@backstage/plugin-app-react';
import {
  BackstageIdentityApi,
  OpenIdConnectApi,
  ProfileInfoApi,
  SessionApi,
} from '@backstage/core-plugin-api';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';

// Custom front-end ApiRef for the OIDC provider exposed by the auth-backend.
// The id ('oidc') must match the provider id wired in
// app-config.production.yaml under `auth.providers.oidc`.
export const oidcAuthApiRef = createApiRef<
  ProfileInfoApi & BackstageIdentityApi & SessionApi & OpenIdConnectApi
>({
  id: 'auth.oidc',
});

const oidcAuthApi = ApiBlueprint.make({
  name: 'oidc',
  params: defineParams =>
    defineParams(
      createApiFactory({
        api: oidcAuthApiRef,
        deps: {
          discoveryApi: discoveryApiRef,
          oauthRequestApi: oauthRequestApiRef,
          configApi: configApiRef,
        },
        factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
          OAuth2.create({
            configApi,
            discoveryApi,
            oauthRequestApi,
            provider: {
              id: 'oidc',
              title: 'Aknostic SSO',
              icon: LockOutlinedIcon,
            },
            environment: configApi.getOptionalString('auth.environment'),
          }),
      }),
    ),
});

// Override the default sign-in page (Guest-only) with one that exposes the
// OIDC button. Guest is kept as a second option to keep local-dev frictionless;
// in prod the Keycloak realm gates access on the backend regardless.
const signInPage = SignInPageBlueprint.make({
  params: {
    loader: async () => {
      const { SignInPage } = await import('@backstage/core-components');
      return props => (
        <SignInPage
          {...props}
          providers={[
            'guest',
            {
              id: 'oidc',
              title: 'Aknostic SSO',
              message: 'Sign in with Keycloak',
              apiRef: oidcAuthApiRef,
            },
          ]}
        />
      );
    },
  },
});

export const authModule = createFrontendModule({
  pluginId: 'app',
  extensions: [oidcAuthApi, signInPage],
});
